import axios from 'axios';
import { API_BASE_URL, handleApiError } from './_apiBase';

/**
 * Avancerade larmregler — sparade inställningar per (sensor, mätvärde) för regler
 * som den vanliga larmmotorn inte kan köra.
 *
 * OBS: raderna lagras bara. Ingenting utvärderar dem ännu, så en sparad regel
 * påverkar inte larmen.
 */

/** Inställningarna för regeltypen 'threshold'. Speglar fälten i tröskel-labbet. */
export interface ThresholdSettings {
    excursionOn: boolean;
    armLevel: number;
    recoverLevel: number;
    ceilingLevel: number;
    deadlineMin: number;
    extendWhileFalling: boolean;

    budgetOn: boolean;
    safeLimit: number;
    /** tillåten tid över gränsen per dygn, i minuter */
    budgetMinutes: number;
}

export interface AdvancedRule {
    id: number;
    parameter_id: number;
    rule_type: 'threshold';
    name: string | null;
    settings: ThresholdSettings;
    enabled: boolean;
    device_id?: number;
    parameter_key?: string;
    parameter_name?: string | null;
    parameter_unit?: string | null;
    ieee_address?: string;
    device_name?: string;
    /** Sensorn är avstängd — regeln körs inte förrän den slås på igen. */
    device_disabled?: boolean;
    updated_at?: string;
    /** recipients, assigned from the Contacts tab */
    contacts?: { id: number; name: string; email: string | null; phone: string | null }[];
    /** Grupper som mottagare: alla i gruppen får larmet. */
    groups?: { id: number; name: string; member_count: number }[];

    instruction_text?: string | null;
    /** the image itself is fetched separately — it is far too big for a list response */
    instruction_has_image?: boolean;
    /** capability URL shown in the notification */
    instruction_token?: string | null;
    requires_deviation_report?: boolean;
}

export interface AdvancedRuleInput {
    parameter_id: number;
    rule_type?: 'threshold';
    name?: string;
    settings: ThresholdSettings;
    enabled?: boolean;
    instruction_text?: string;
    /** omit to leave an existing image untouched; '' clears it */
    instruction_image?: string;
    requires_deviation_report?: boolean;
}

export const fetchAdvancedRules = async (): Promise<AdvancedRule[]> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/advanced-rules`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta avancerade regler'); }
};

/** Skapar eller uppdaterar regeln för (parameter_id, rule_type). */
export const saveAdvancedRule = async (data: AdvancedRuleInput): Promise<AdvancedRule> => {
    try {
        const res = await axios.post(`${API_BASE_URL}/advanced-rules`, data);
        return res.data;
    } catch (error) { return handleApiError(error, 'Spara avancerad regel'); }
};

/** Fetched on demand — the base64 payload is too large for the list response. */
export const fetchAdvancedRuleImage = async (id: number): Promise<string | null> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/advanced-rules/${id}/image`);
        return res.data?.instruction_image ?? null;
    } catch (error) { return handleApiError(error, 'Hämta bild till regeln'); }
};

/** Se deleteAlertRule: 409 med historyCount betyder att inget raderats än. */
export const deleteAdvancedRule = async (id: number, force = false): Promise<{ historyCount: number } | void> => {
    try {
        await axios.delete(`${API_BASE_URL}/advanced-rules/${id}`, { params: force ? { force: 1 } : {} });
    } catch (error: any) {
        if (error?.response?.status === 409 && error.response.data?.historyCount != null) {
            return { historyCount: Number(error.response.data.historyCount) };
        }
        return handleApiError(error, 'Ta bort avancerad regel');
    }
};
